import React, { useEffect } from "react";
import { Link, Outlet } from "react-router-dom";
import { LogIn, SlidersHorizontal } from "lucide-react";
import brand from "lib/brand";
import { useAuth } from "contexts/AuthContext";
import { useI18n, LANGUAGE_OPTIONS } from "i18n";

/**
 * Casca da AREA PUBLICA (`/`) — a area padrao do sistema.
 *
 * Anonima: nao consulta `/api/auth/me/`, nao guarda token e nao conhece
 * usuario. O unico caminho daqui para a area administrativa e o botao "Entrar",
 * que leva a `/app/login`.
 *
 * Tema: a vitrine e sempre clara. Como o modo escuro do painel escreve a classe
 * `dark` no `<html>`, removemos a classe ao montar.
 */
export default function PublicLayout() {
  const { t, lang } = useI18n();
  // `changeLanguage` grava o cookie do visitante e, se houver sessao ativa
  // (token no navegador, ainda que o login tenha sido feito em outra aba),
  // tambem no perfil. Sem isso o refresh voltava ao idioma do perfil: o
  // AuthProvider e global, entao a vitrine tambem resolve o `/api/auth/me/`.
  const { changeLanguage } = useAuth();

  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white text-neutral-900">
      <header className="sticky top-0 z-40 border-b border-neutral-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <img src={brand.logo} alt={brand.name} className="h-7 w-auto" />
          </Link>

          <nav className="flex items-center gap-3 sm:gap-5">
            <Link
              to="/"
              className="text-sm font-medium text-neutral-600 transition-colors hover:text-neutral-900"
            >
              {t("public.nav.events")}
            </Link>

            {/* Hotsite estatico servido pelo nginx (./photoeditor-hotsite), fora
                do SPA: `<a>` comum, para o navegador sair do React Router. */}
            <a
              href="/photo-editor/"
              aria-label={t("public.nav.photoEditor", "Editor de fotos")}
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-neutral-600 transition-colors hover:text-neutral-900"
            >
              <SlidersHorizontal size={16} className="sm:hidden" />
              <span className="hidden sm:inline">
                {t("public.nav.photoEditor", "Editor de fotos")}
              </span>
            </a>

            <select
              value={lang}
              onChange={(e) => changeLanguage(e.target.value)}
              aria-label={t("common.language")}
              className="cursor-pointer bg-transparent text-sm text-neutral-600 focus:outline-none hover:text-neutral-900"
            >
              {LANGUAGE_OPTIONS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>

            <Link
              to="/app/login"
              className="inline-flex items-center gap-2 rounded-full bg-brand-signin px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-signin-hover"
            >
              <LogIn size={16} />
              <span className="hidden sm:inline">{t("public.nav.signIn")}</span>
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-neutral-200 bg-neutral-50">
        <div className="mx-auto w-full max-w-7xl px-4 pt-8 sm:px-6">
          <h2 className="text-base font-semibold text-neutral-900">
            {t("public.footer.aboutTitle", "Feito por um pai, para o esporte. 🏀📸")}
          </h2>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-neutral-600">
            <p>{t("public.footer.aboutP1")}</p>
            <p>{t("public.footer.aboutP2")}</p>
            <p>{t("public.footer.aboutP3")}</p>
          </div>
        </div>

        <div className="mx-auto mt-8 flex w-full max-w-7xl flex-col gap-2 border-t border-neutral-200 px-4 py-6 text-sm text-neutral-500 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <span>
            &copy; {new Date().getFullYear()} {brand.name}
          </span>
          <a
            href={`mailto:${brand.contactEmail}`}
            className="transition-colors hover:text-neutral-900"
          >
            {brand.contactEmail}
          </a>
        </div>
      </footer>
    </div>
  );
}
